import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { attT } from "@/lib/attendanceI18n";
import GeofenceTab from "@/pages/admin/attendance/GeofenceTab";
import TimesheetTab from "@/pages/admin/attendance/TimesheetTab";
import WorkRulesTab from "@/pages/admin/attendance/WorkRulesTab";
import LeavePeriodsTab from "@/pages/admin/attendance/LeavePeriodsTab";

/**
 * Unified Attendance Admin (single icon, four tabs).
 * Tabs: Timesheet (KTZ) | Leaves | Work Rules | Geofence.
 * Visible to: STAS + GM + Deputy + Accountant (read-only).
 */
export default function AttendanceAdminPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, dir, lang } = useI18n();

  const isRoot = !!user?.role?.is_root;
  const roleCode = user?.role?.code;
  const canEdit = isRoot || ["general_manager", "deputy_manager"].includes(roleCode);
  const canView = canEdit || roleCode === "accountant";

  const [active, setActive] = useState("timesheet");

  const TABS = [
    { key: "timesheet",  label: attT(lang, "tab.timesheet"), testId: "tab-timesheet" },
    { key: "leaves",     label: attT(lang, "tab.leaves"),    testId: "tab-leaves" },
    { key: "rules",      label: attT(lang, "tab.rules"),     testId: "tab-rules" },
    { key: "geofence",   label: attT(lang, "tab.geofence"),  testId: "tab-geofence" },
  ];

  if (!canView) {
    return <div style={{ padding: 40, color: theme.errorText }} dir={dir}>{attT(lang, "stas_only")}</div>;
  }

  const css = makeStyles(theme);

  return (
    <div dir={dir} style={css.page} data-testid="attendance-admin-page">
      <div style={css.container}>
        <div style={css.header}>
          <button onClick={() => navigate("/")} style={css.backBtn} data-testid="attendance-back">
            {attT(lang, dir === "rtl" ? "back" : "back_ltr")}
          </button>
          <h1 style={css.title}>{attT(lang, "title")}</h1>
        </div>

        <div style={css.tabs} role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActive(tab.key)}
              style={{ ...css.tab, ...(active === tab.key ? css.tabActive : {}) }}
              role="tab"
              aria-selected={active === tab.key}
              data-testid={tab.testId}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div style={css.body}>
          {active === "timesheet" && <TimesheetTab canEdit={canEdit} />}
          {active === "leaves"    && <LeavePeriodsTab canEdit={canEdit} />}
          {active === "rules"     && <WorkRulesTab canEdit={canEdit} />}
          {active === "geofence"  && <GeofenceTab canEdit={isRoot} />}
        </div>
      </div>
    </div>
  );
}

const makeStyles = (theme) => ({
  page: { minHeight: "100vh", background: theme.bg, color: theme.text, fontFamily: "'Tajawal','Segoe UI', sans-serif" },
  container: { maxWidth: 1280, margin: "0 auto", padding: "24px 16px" },
  header: { display: "flex", gap: 16, alignItems: "center", marginBottom: 18 },
  backBtn: { background: "transparent", color: theme.text, border: `1px solid ${theme.border}`, padding: "8px 14px", borderRadius: 8, cursor: "pointer" },
  title: { margin: 0, fontSize: 22, fontWeight: 800, color: theme.gold },
  tabs: { display: "flex", gap: 6, borderBottom: `1px solid ${theme.border}`, marginBottom: 18, overflowX: "auto", paddingBottom: 1 },
  tab: { background: "transparent", color: theme.textMuted, border: "none", borderBottomWidth: 3, borderBottomStyle: "solid", borderBottomColor: "transparent", padding: "10px 18px", cursor: "pointer", fontWeight: 700, fontSize: 14, fontFamily: "inherit", whiteSpace: "nowrap", transition: "color 120ms, border-bottom-color 120ms" },
  tabActive: { color: theme.gold, borderBottomColor: theme.gold },
  body: { minHeight: 320 },
});
